// src/components/Atelier4/ModesOperatoiresFlowVisualization.js
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import ReactFlow, {
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    MarkerType,
    Handle,
    Position,
    getRectOfNodes,
    getTransformForBounds,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { X, Info, Layers, Download, BrainCircuit } from 'lucide-react';
import { getVraisemblanceColor, getVraisemblanceBgColor } from '../../utils/vraisemblanceCalculator';
import { toPng } from 'html-to-image';

/**
 * Custom Node Component for Attack Actions
 * Enhanced to show PS, DT, V and detailed information with connection handles
 */
const ActionNode = ({ data }) => {
    const { t } = useTranslation();
    const getPhaseColor = (phase) => {
        switch (phase) {
            case 'CONNAITRE': return '#3B82F6';
            case 'RENTRER': return '#EF4444';
            case 'TROUVER': return '#F59E0B';
            case 'EXPLOITER': return '#10B981';
            default: return '#6B7280';
        }
    };

    const phaseColor = getPhaseColor(data.phase);

    // Get PS/DT colors
    const getPSDTColor = (value) => {
        if (value >= 4) return '#EF4444'; // Red
        if (value >= 3) return '#F59E0B'; // Orange
        if (value >= 2) return '#FCD34D'; // Yellow
        return '#10B981'; // Green
    };

    const ps = data.ps || data.potentielSurprise || 2;
    const dt = data.dt || data.detectabilite || 2;

    return (
        <div
            className="shadow-xl rounded-xl border-3 bg-white min-w-[280px] max-w-[320px] overflow-hidden relative"
            style={{ borderColor: phaseColor, borderWidth: '3px' }}
        >
            {/* Input Handle (for incoming edges) */}
            <Handle
                type="target"
                position={Position.Left}
                style={{ background: phaseColor, width: 12, height: 12, left: -6 }}
            />

            {/* Header with Phase */}
            <div
                className="px-3 py-2 flex items-center justify-between"
                style={{ backgroundColor: phaseColor }}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <div className="text-white font-bold text-sm">
                        {data.phase}
                    </div>
                    {data.isAiGenerated && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/25 text-white text-[9px] font-semibold whitespace-nowrap">
                            <BrainCircuit className="h-2.5 w-2.5" />
                            {t('workshop4.modesOperatoires.aiBadge', 'Assistant ACG')}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {data.modesUsing && data.modesUsing.length > 0 && (
                        <div className="flex gap-1">
                            {data.modesUsing.map((modeIdx) => (
                                <span
                                    key={modeIdx}
                                    className="text-[9px] px-1.5 py-0.5 rounded bg-white bg-opacity-30 text-white font-bold"
                                >
                                    M{modeIdx + 1}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="px-3 py-3">
                {/* Technique/Action Name */}
                <div className="font-bold text-sm text-slate-900 mb-2 leading-tight">
                    {data.technique || data.name || 'Action non nommée'}
                </div>

                {/* Description */}
                {data.description && (
                    <div className="text-xs text-slate-600 mb-3 line-clamp-2 leading-snug">
                        {data.description}
                    </div>
                )}

                {/* Metrics Grid - PS, DT */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    {/* PS */}
                    <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                        <div className="text-[9px] text-slate-500 font-semibold mb-0.5">PS</div>
                        <div
                            className="text-sm font-bold"
                            style={{ color: getPSDTColor(ps) }}
                        >
                            {ps}/4
                        </div>
                    </div>

                    {/* DT */}
                    <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
                        <div className="text-[9px] text-slate-500 font-semibold mb-0.5">DT</div>
                        <div
                            className="text-sm font-bold"
                            style={{ color: getPSDTColor(dt) }}
                        >
                            {dt}/4
                        </div>
                    </div>
                </div>

                {/* Additional Info */}
                <div className="space-y-1">
                    {/* Duration */}
                    {data.duration && (
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                            <span className="text-slate-400">⏱️</span>
                            <span className="font-medium">{data.duration}</span>
                        </div>
                    )}

                    {/* Tools */}
                    {data.tools && data.tools.length > 0 && (
                        <div className="flex items-start gap-1 text-xs">
                            <span className="text-slate-400">🛠️</span>
                            <div className="flex-1">
                                <div className="flex flex-wrap gap-1">
                                    {data.tools.slice(0, 2).map((tool, idx) => (
                                        <span
                                            key={idx}
                                            className="inline-block px-1.5 py-0.5 bg-green-50 text-green-700 rounded text-[10px] font-medium border border-green-200"
                                        >
                                            {typeof tool === 'string' ? tool : tool.name}
                                        </span>
                                    ))}
                                    {data.tools.length > 2 && (
                                        <span className="text-[10px] text-slate-500 italic">
                                            +{data.tools.length - 2}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer - Elementary Action ID */}
            {data.elementaryActionId && (
                <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200">
                    <div className="text-[9px] text-slate-400 font-mono">
                        {data.elementaryActionId}
                    </div>
                </div>
            )}

            {/* Output Handle (for outgoing edges) */}
            <Handle
                type="source"
                position={Position.Right}
                style={{ background: phaseColor, width: 12, height: 12, right: -6 }}
            />
        </div>
    );
};

const nodeTypes = {
    actionNode: ActionNode,
};

/**
 * ModesOperatoiresFlowVisualization Component
 * Displays all modes operatoires in a React Flow graph with sequence-based columnar layout
 * Uses React Portal to render at document.body level for true full-screen display
 */
const ModesOperatoiresFlowVisualization = ({
    modesOperatoires = [],
    operationalSteps = [],
    scenario = null,
    onClose,
    isInline = false,
    className = "",
    onCaptureReady = null
}) => {
    const { t } = useTranslation();
    const [selectedMode, setSelectedMode] = useState(null);
    const [showInfo, setShowInfo] = useState(true);
    const flowWrapper = React.useRef(null);

    // Non-retenu modes are excluded from the graph entirely
    const visibleModes = React.useMemo(
        () => (modesOperatoires || []).filter((m) => m && m.retained !== false),
        [modesOperatoires]
    );

    const downloadImage = () => {
        if (!flowWrapper.current) return;

        // Find the viewport element inside the wrapper
        const viewport = flowWrapper.current.querySelector('.react-flow__viewport');

        if (!viewport) {
            console.error("Viewport not found");
            return;
        }

        // Calculate bounds of all nodes to determine image size
        const nodesBounds = getRectOfNodes(nodes);

        // Add some padding
        const padding = 50;
        const width = nodesBounds.width + (padding * 2);
        const height = nodesBounds.height + (padding * 2);

        toPng(viewport, {
            backgroundColor: '#f8fafc', // Match bg-slate-50
            width: width,
            height: height,
            style: {
                width: width + 'px',
                height: height + 'px',
                // Shift content so it starts at (padding, padding)
                transform: `translate(${-nodesBounds.x + padding}px, ${-nodesBounds.y + padding}px) scale(1)`,
            },
            pixelRatio: 3, // High resolution (3x)
        })
            .then((dataUrl) => {
                const link = document.createElement('a');
                link.download = 'modes-operatoires-visualization.png';
                link.href = dataUrl;
                link.click();
            })
            .catch((err) => {
                console.error('Failed to export image:', err);
            });
    };

    // Build graph from modes operatoires with sequence-based columnar layout
    const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
        if (!visibleModes || visibleModes.length === 0) {
            return { nodes: [], edges: [] };
        }

        // Collect all unique actions with their positions in paths
        const actionMap = new Map();
        const edgeSet = new Set();

        visibleModes.forEach((mode, modeIdx) => {
            if (!mode.path) return;
            const isAiMode = String(mode.id || '').startsWith('mode_ai_') || (scenario?.metadata?.generatedByAI && String(mode.id || '').startsWith('mo_'));

            mode.path.forEach((actionId, stepIdx) => {
                // Find action details - try multiple ID matching strategies
                // Support both AI-generated (elem_1, elem_2) and manual (action_12345) ID formats
                let action = operationalSteps.find(s =>
                    s.elementaryActionId === actionId ||
                    s.id === actionId ||
                    s._id === actionId
                );

                // Debug logging for troubleshooting
                if (!action && operationalSteps.length > 0) {
                    console.debug(`[ModesOperatoiresFlowVisualization] Action not found for ID: ${actionId}`);
                    console.debug(`[ModesOperatoiresFlowVisualization] Available IDs:`, operationalSteps.map(s => s.elementaryActionId || s.id));
                }

                if (!action) return;

                // Add or update action in map
                if (!actionMap.has(actionId)) {
                    actionMap.set(actionId, {
                        ...action, // Spread original action first
                        // Then explicitly set/override id values to ensure edges connect properly
                        id: actionId,
                        elementaryActionId: actionId,
                        modesUsing: [modeIdx],
                        minPosition: stepIdx,
                        maxPosition: stepIdx,
                        isAiGenerated: isAiMode
                    });
                } else {
                    const existing = actionMap.get(actionId);
                    if (isAiMode) existing.isAiGenerated = true;
                    if (!existing.modesUsing.includes(modeIdx)) {
                        existing.modesUsing.push(modeIdx);
                    }
                    // Track min/max positions across all modes
                    existing.minPosition = Math.min(existing.minPosition, stepIdx);
                    existing.maxPosition = Math.max(existing.maxPosition, stepIdx);
                }

                // Add edge to next action
                if (stepIdx < mode.path.length - 1) {
                    const nextActionId = mode.path[stepIdx + 1];
                    const edgeId = `${actionId}-${nextActionId}-${modeIdx}`;
                    edgeSet.add(JSON.stringify({
                        id: edgeId,
                        source: actionId,
                        target: nextActionId,
                        modeIdx,
                        vraisemblance: mode.vraisemblance || 2
                    }));
                }
            });
        });

        // Group actions by their earliest position (column)
        const columnGroups = new Map();
        Array.from(actionMap.values()).forEach(action => {
            const column = action.minPosition;
            if (!columnGroups.has(column)) {
                columnGroups.set(column, []);
            }
            columnGroups.get(column).push(action);
        });

        // Calculate positions - sequence-based columnar layout
        const columnWidth = 400; // Space between columns
        const rowHeight = 320; // Space between rows (for parallel actions)
        const startX = 50;
        const startY = 50;

        const nodes = [];
        const sortedColumns = Array.from(columnGroups.keys()).sort((a, b) => a - b);

        sortedColumns.forEach((columnIdx, visualColumnIdx) => {
            const actionsInColumn = columnGroups.get(columnIdx);
            const columnX = startX + (visualColumnIdx * columnWidth);

            // Sort actions in column by phase for consistent vertical ordering
            const phaseOrder = { 'CONNAITRE': 0, 'RENTRER': 1, 'TROUVER': 2, 'EXPLOITER': 3 };
            actionsInColumn.sort((a, b) => {
                const phaseA = phaseOrder[a.phase] ?? 4;
                const phaseB = phaseOrder[b.phase] ?? 4;
                return phaseA - phaseB;
            });

            actionsInColumn.forEach((action, rowIdx) => {
                const rowY = startY + (rowIdx * rowHeight);

                nodes.push({
                    // Use elementaryActionId as node ID because edges reference this
                    id: action.elementaryActionId || action.id,
                    type: 'actionNode',
                    position: { x: columnX, y: rowY },
                    data: {
                        // Support both AI ('technique') and manual ('name') action titles
                        technique: action.technique || action.name,
                        name: action.name || action.technique,
                        phase: action.phase,
                        vraisemblance: action.vraisemblance,
                        // Support both AI (successProbability/technicalDifficulty) and manual (ps/dt) fields
                        ps: action.ps || action.successProbability || action.potentielSurprise || 2,
                        dt: action.dt || action.technicalDifficulty || action.detectabilite || 2,
                        duration: action.duration,
                        tools: action.tools || [],
                        description: action.description,
                        elementaryActionId: action.elementaryActionId,
                        modesUsing: action.modesUsing,
                        isAiGenerated: action.isAiGenerated,
                    },
                });
            });
        });

        // Convert to edges array with enhanced styling
        const edges = Array.from(edgeSet).map(edgeStr => {
            const edge = JSON.parse(edgeStr);
            const vColor = getVraisemblanceColor(edge.vraisemblance);

            return {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                type: 'smoothstep', // Use smoothstep for better routing
                animated: selectedMode === null || selectedMode === edge.modeIdx,
                style: {
                    stroke: selectedMode === edge.modeIdx ? vColor : (selectedMode === null ? vColor : '#e2e8f0'),
                    strokeWidth: selectedMode === edge.modeIdx ? 4 : (selectedMode === null ? 3 : 1),
                    opacity: selectedMode === null || selectedMode === edge.modeIdx ? 1 : 0.2,
                },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 30,
                    height: 30,
                    color: selectedMode === edge.modeIdx ? vColor : (selectedMode === null ? vColor : '#e2e8f0'),
                },
                label: selectedMode === edge.modeIdx || selectedMode === null ? `M${edge.modeIdx + 1}` : '',
                labelStyle: {
                    fill: vColor,
                    fontWeight: 700,
                    fontSize: 11,
                },
                labelBgStyle: { fill: 'white', fillOpacity: 0.9 },
                data: { modeIdx: edge.modeIdx },
            };
        });
        // Debug logging for troubleshooting
        console.debug('[ModesOperatoiresFlowVisualization] Generated graph:', {
            nodesCount: nodes.length,
            edgesCount: edges.length,
            nodeIds: nodes.map(n => n.id),
            edgeConnections: edges.map(e => `${e.source} -> ${e.target}`)
        });

        return { nodes, edges };
    }, [visibleModes, operationalSteps, selectedMode]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    // Update nodes opacity based on selected mode
    useMemo(() => {
        if (selectedMode === null) {
            setNodes(initialNodes);
        } else {
            setNodes(initialNodes.map(node => ({
                ...node,
                style: {
                    opacity: node.data.modesUsing.includes(selectedMode) ? 1 : 0.3,
                }
            })));
        }
    }, [selectedMode, initialNodes, setNodes]);

    // Update edges when initialEdges changes (critical for manual scenarios)
    useMemo(() => {
        setEdges(initialEdges);
    }, [initialEdges, setEdges]);

    const captureAndSendPng = useCallback(async () => {
        if (!flowWrapper.current || nodes.length === 0) return null;
        const width = flowWrapper.current.offsetWidth || 900;
        const height = flowWrapper.current.offsetHeight || 500;
        try {
            const dataUrl = await toPng(flowWrapper.current, {
                backgroundColor: '#f8fafc',
                width, height,
                pixelRatio: 3,
            });
            return { dataUrl, width, height };
        } catch (err) {
            console.error('Failed to capture PNG:', err);
            return null;
        }
    }, [nodes]);

    // Register the capture function — the parent triggers it at save time
    useEffect(() => {
        onCaptureReady?.(captureAndSendPng);
        return () => onCaptureReady?.(null);
    }, [onCaptureReady, captureAndSendPng]);

    if (!visibleModes || visibleModes.length === 0) {
        return null;
    }

    // If inline, render directly without portal
    if (isInline) {
        return (
            <div className={`bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col h-[600px] ${className}`}>
                {/* Header */}
                <div className="bg-white px-6 py-4 border-b border-slate-200 rounded-t-xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-bold text-slate-800 flex items-center">
                                <Layers className="mr-2 text-indigo-600" size={20} />
                                {t('workshop4.visualization.title', 'Graphical Visualization of Operational Modes')}
                            </h3>
                            <p className="text-slate-500 text-sm mt-0.5">
                                {t('workshop4.visualization.subtitle', '{count} attack variants • Sequential organization').replace('{count}', visibleModes.length)}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={downloadImage}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-600 hover:text-indigo-600"
                                title="Exporter en PNG Haute Résolution"
                            >
                                <Download size={20} />
                            </button>
                            {onClose && (
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <X className="text-slate-500" size={20} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Mode Selector */}
                <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-700">{t('workshop4.visualization.filterByMode', 'Filter by mode:')}</span>
                        <button
                            onClick={() => setSelectedMode(null)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${selectedMode === null
                                ? 'bg-indigo-500 text-white shadow-md'
                                : 'bg-white text-slate-700 border border-slate-300 hover:border-indigo-400'
                                }`}
                        >
                            {t('workshop4.visualization.allModes', 'All modes')}
                        </button>
                        {visibleModes.map((mode, idx) => {
                            const vColor = getVraisemblanceColor(mode.vraisemblance || 2);
                            return (
                                <button
                                    key={idx}
                                    onClick={() => setSelectedMode(idx)}
                                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${selectedMode === idx
                                        ? 'text-white shadow-md'
                                        : 'bg-white text-slate-700 border border-slate-300 hover:border-indigo-400'
                                        }`}
                                    style={selectedMode === idx ? { backgroundColor: vColor } : {}}
                                >
                                    Mode {idx + 1} (V:{mode.vraisemblance}/4)
                                </button>
                            );
                        })}
                        <button
                            onClick={() => setShowInfo(!showInfo)}
                            className="ml-auto p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            title="Afficher/Masquer l'aide"
                        >
                            <Info size={18} className="text-slate-600" />
                        </button>
                    </div>
                </div>

                {/* Info Panel removed */}

                {/* React Flow Canvas */}
                <div className="flex-1 bg-slate-50 relative" ref={flowWrapper}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        nodeTypes={nodeTypes}
                        fitView
                        fitViewOptions={{ padding: 0.2, includeHiddenNodes: true }}
                        minZoom={0.1}
                        maxZoom={1.5}
                        defaultEdgeOptions={{ type: 'smoothstep' }}
                        attributionPosition="bottom-left"
                    >
                        <Background color="#cbd5e1" gap={16} />
                        <Controls />
                    </ReactFlow>
                </div>

                {/* Legend */}
                <div className="px-6 py-3 bg-white border-t border-slate-200 rounded-b-xl">
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-slate-700">{t('workshop4.visualization.ebiosRmPhases', 'EBIOS RM Phases:')}</span>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }}></div>
                                <span className="text-slate-600">CONNAITRE</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }}></div>
                                <span className="text-slate-600">RENTRER</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
                                <span className="text-slate-600">TROUVER</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10B981' }}></div>
                                <span className="text-slate-600">EXPLOITER</span>
                            </div>
                        </div>
                        <div className="text-slate-500">
                            {nodes.length} {t('workshop4.modesOperatoires.actions', 'actions')} • {edges.length} {t('workshop4.visualization.transitions', 'transitions')}
                        </div>
                    </div>
                </div>
            </div >
        );
    }

    // Use React Portal to render modal at document.body level - bypasses all parent containers
    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-2xl w-[95vw] h-[90vh] flex flex-col">
                {/* Header */}
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-4 rounded-t-xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-white flex items-center">
                                <Layers className="mr-3" size={24} />
                                {t('workshop4.visualization.title', 'Graphical Visualization of Operational Modes')}
                            </h3>
                            <p className="text-indigo-100 text-sm mt-1">
                                {t('workshop4.visualization.subtitle', '{count} attack variants • Sequential organization').replace('{count}', visibleModes.length)}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={downloadImage}
                                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors text-white"
                                title="Exporter en PNG Haute Résolution"
                            >
                                <Download size={24} />
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                            >
                                <X className="text-white" size={24} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mode Selector */}
                <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-700">{t('workshop4.visualization.filterByMode', 'Filter by mode:')}</span>
                        <button
                            onClick={() => setSelectedMode(null)}
                            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${selectedMode === null
                                ? 'bg-indigo-500 text-white shadow-md'
                                : 'bg-white text-slate-700 border border-slate-300 hover:border-indigo-400'
                                }`}
                        >
                            {t('workshop4.visualization.allModes', 'All modes')}
                        </button>
                        {visibleModes.map((mode, idx) => {
                            const vColor = getVraisemblanceColor(mode.vraisemblance || 2);
                            return (
                                <button
                                    key={idx}
                                    onClick={() => setSelectedMode(idx)}
                                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${selectedMode === idx
                                        ? 'text-white shadow-md'
                                        : 'bg-white text-slate-700 border border-slate-300 hover:border-indigo-400'
                                        }`}
                                    style={selectedMode === idx ? { backgroundColor: vColor } : {}}
                                >
                                    {t('workshop4.modesOperatoires.mode', 'Mode')} {idx + 1} (V:{mode.vraisemblance}/4)
                                </button>
                            );
                        })}
                        <button
                            onClick={() => setShowInfo(!showInfo)}
                            className="ml-auto p-2 hover:bg-slate-200 rounded-lg transition-colors"
                            title="Afficher/Masquer l'aide"
                        >
                            <Info size={18} className="text-slate-600" />
                        </button>
                    </div>
                </div>

                {/* Info Panel removed */}

                {/* React Flow Canvas */}
                <div className="flex-1 bg-slate-50 relative" ref={flowWrapper}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        nodeTypes={nodeTypes}
                        fitView
                        fitViewOptions={{ padding: 0.2, includeHiddenNodes: true }}
                        minZoom={0.1}
                        maxZoom={1.5}
                        defaultEdgeOptions={{ type: 'smoothstep' }}
                        attributionPosition="bottom-left"
                    >
                        <Background color="#cbd5e1" gap={16} />
                        <Controls />
                    </ReactFlow>
                </div>

                {/* Legend */}
                <div className="px-6 py-3 bg-white border-t border-slate-200">
                    <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-4">
                            <span className="font-semibold text-slate-700">{t('workshop4.visualization.ebiosRmPhases', 'EBIOS RM Phases:')}</span>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }}></div>
                                <span className="text-slate-600">CONNAITRE</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }}></div>
                                <span className="text-slate-600">RENTRER</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F59E0B' }}></div>
                                <span className="text-slate-600">TROUVER</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10B981' }}></div>
                                <span className="text-slate-600">EXPLOITER</span>
                            </div>
                        </div>
                        <div className="text-slate-500">
                            {nodes.length} {t('workshop4.modesOperatoires.actions', 'actions')} • {edges.length} {t('workshop4.visualization.transitions', 'transitions')}
                        </div>
                    </div>
                </div>
            </div>
        </div >,
        document.body
    );
};

export default ModesOperatoiresFlowVisualization;
